import React, { useState, useEffect } from 'react';
import './AuditLogHistory.css';
import { fetchAuditLog } from '../../services/api/fetchGrouper';

function AuditLogHistory({ clippingId }) {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroupers, setExpandedGroupers] = useState({});

  useEffect(() => {
    const loadAuditLogs = async () => {
      if (!clippingId) return;

      try {
        setLoading(true);
        const data = await fetchAuditLog(clippingId);
        setAuditLogs(data || []);
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar histórico:', err);
        setError('Erro ao carregar histórico de avaliações');
      } finally {
        setLoading(false);
      }
    };

    loadAuditLogs();
  }, [clippingId]);

  const groupByGrouper = (logs) => {
    const grouped = {};
    logs.forEach(log => {
      const grouperName = log.grouper_name || 'Desconhecido';
      if (!grouped[grouperName]) {
        grouped[grouperName] = [];
      }
      grouped[grouperName].push(log);
    });
    return grouped;
  };

  const toggleGrouper = (grouperName) => {
    setExpandedGroupers(prev => ({
      ...prev,
      [grouperName]: !prev[grouperName]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'ai_evaluated':
        return '🤖';
      case 'human_evaluated':
        return '👤';
      case 'approved':
        return '✅';
      case 'revised':
        return '📝';
      case 'rejected':
        return '❌';
      default:
        return '📋';
    }
  };

  const getActionClass = (action) => {
    switch (action) {
      case 'ai_evaluated':
        return 'action-ai';
      case 'human_evaluated':
        return 'action-human';
      case 'approved':
        return 'action-approved';
      case 'revised':
        return 'action-revised';
      case 'rejected':
        return 'action-rejected';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="audit-log-loading">
        <span>Carregando histórico...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="audit-log-error">
        <span>{error}</span>
      </div>
    );
  }

  if (auditLogs.length === 0) {
    return (
      <div className="audit-log-empty">
        <span>Nenhum histórico de avaliação encontrado.</span>
      </div>
    );
  }

  const groupedLogs = groupByGrouper(auditLogs);

  return (
    <div className="audit-log-container">
      <h3 className="audit-log-title">Histórico de Avaliações</h3>

      <div className="audit-log-groups">
        {Object.entries(groupedLogs).map(([grouperName, logs]) => (
          <div key={grouperName} className="audit-log-group">
            <div
              className="audit-log-group-header"
              onClick={() => toggleGrouper(grouperName)}
            >
              <span className="audit-log-group-name">{grouperName}</span>
              <span className="audit-log-group-count">{logs.length} alteração(ões)</span>
              <span className="audit-log-group-toggle">
                {expandedGroupers[grouperName] ? '▼' : '▶'}
              </span>
            </div>

            {expandedGroupers[grouperName] && (
              <div className="audit-log-entries">
                {logs.map((log, index) => (
                  <div key={log.id || index} className={`audit-log-entry ${getActionClass(log.action)}`}>
                    <div className="audit-log-entry-header">
                      <span className="audit-log-action-icon">{getActionIcon(log.action)}</span>
                      <span className="audit-log-action-label">{log.action_display}</span>
                      <span className="audit-log-date">{formatDate(log.created)}</span>
                    </div>

                    <div className="audit-log-entry-body">
                      <div className="audit-log-values">
                        {log.old_value && (
                          <div className="audit-log-old-value">
                            <span className="audit-log-value-label">Valor anterior:</span>
                            <span className="audit-log-value-text">{log.old_value}</span>
                          </div>
                        )}
                        <div className="audit-log-new-value">
                          <span className="audit-log-value-label">
                            {log.old_value ? 'Novo valor:' : 'Valor:'}
                          </span>
                          <span className="audit-log-value-text">{log.new_value}</span>
                        </div>
                      </div>

                      <div className="audit-log-meta">
                        <span className="audit-log-author">
                          Por: {log.changed_by_name || 'Sistema'}
                        </span>
                        {log.is_ai_to_human_change && (
                          <span className="audit-log-ai-correction">
                            {log.ai_was_correct ? 'IA correta' : 'IA corrigida'}
                          </span>
                        )}
                      </div>

                      {log.notes && (
                        <div className="audit-log-notes">
                          <span className="audit-log-notes-label">Obs:</span>
                          <span className="audit-log-notes-text">{log.notes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default AuditLogHistory;
